import mapboxgl from "mapbox-gl";
import type { AssignmentArea } from "../api/types";

export const ADMIN_ASSIGNMENT_HIGHLIGHT_SOURCE_ID = "admin-assignment-highlight";
export const ADMIN_ASSIGNMENT_HIGHLIGHT_FILL_LAYER_ID = "admin-assignment-highlight-fill";
export const ADMIN_ASSIGNMENT_HIGHLIGHT_LINE_LAYER_ID = "admin-assignment-highlight-line";

/**
 * Draw or update the read-only assignment polygon on the map (admin basemap mode).
 * Returns false if the area is invalid or the style is not ready.
 */
export function applyAdminAssignmentHighlightToMap(
    map: mapboxgl.Map,
    area: AssignmentArea,
    options?: { fitBounds?: boolean }
): boolean {
    const fitBounds = options?.fitBounds !== false;
    const ring = area.coordinates?.[0];
    if (!ring || ring.length < 4) {
        return false;
    }
    if (!map.isStyleLoaded()) {
        return false;
    }

    const geojson = {
        type: "FeatureCollection" as const,
        features: [
            {
                type: "Feature" as const,
                properties: {},
                geometry: {
                    type: "Polygon" as const,
                    coordinates: area.coordinates!
                }
            }
        ]
    };

    // Insert before the first symbol layer so our fills don't cover building extrusions or text labels.
    const firstSymbol = map.getStyle()?.layers?.find((l) => l.type === "symbol")?.id;

    try {
        if (!map.getSource(ADMIN_ASSIGNMENT_HIGHLIGHT_SOURCE_ID)) {
            map.addSource(ADMIN_ASSIGNMENT_HIGHLIGHT_SOURCE_ID, { type: "geojson", data: geojson });
            map.addLayer({
                id: ADMIN_ASSIGNMENT_HIGHLIGHT_FILL_LAYER_ID,
                type: "fill",
                source: ADMIN_ASSIGNMENT_HIGHLIGHT_SOURCE_ID,
                paint: {
                    "fill-color": "#2b8a3e",
                    "fill-opacity": 0.34
                }
            }, firstSymbol);
            map.addLayer({
                id: ADMIN_ASSIGNMENT_HIGHLIGHT_LINE_LAYER_ID,
                type: "line",
                source: ADMIN_ASSIGNMENT_HIGHLIGHT_SOURCE_ID,
                paint: {
                    "line-color": "#1b5e20",
                    "line-width": 3
                }
            }, firstSymbol);
        } else {
            (map.getSource(ADMIN_ASSIGNMENT_HIGHLIGHT_SOURCE_ID) as mapboxgl.GeoJSONSource).setData(geojson);
            if (!map.getLayer(ADMIN_ASSIGNMENT_HIGHLIGHT_FILL_LAYER_ID)) {
                map.addLayer({
                    id: ADMIN_ASSIGNMENT_HIGHLIGHT_FILL_LAYER_ID,
                    type: "fill",
                    source: ADMIN_ASSIGNMENT_HIGHLIGHT_SOURCE_ID,
                    paint: {
                        "fill-color": "#2b8a3e",
                        "fill-opacity": 0.34
                    }
                }, firstSymbol);
            }
            if (!map.getLayer(ADMIN_ASSIGNMENT_HIGHLIGHT_LINE_LAYER_ID)) {
                map.addLayer({
                    id: ADMIN_ASSIGNMENT_HIGHLIGHT_LINE_LAYER_ID,
                    type: "line",
                    source: ADMIN_ASSIGNMENT_HIGHLIGHT_SOURCE_ID,
                    paint: {
                        "line-color": "#1b5e20",
                        "line-width": 3
                    }
                }, firstSymbol);
            }
        }

        if (fitBounds) {
            const bounds = new mapboxgl.LngLatBounds();
            for (const coord of ring) {
                if (coord.length >= 2) {
                    bounds.extend([coord[0], coord[1]]);
                }
            }
            if (!bounds.isEmpty()) {
                map.fitBounds(bounds, { padding: 56, maxZoom: 16, duration: 600 });
            }
        }

        // Keep layer order: overview fill → overview line → highlight fill → highlight line → [symbols]
        const overviewFillId = "admin-assignments-overview-fill";
        const overviewLineId = "admin-assignments-overview-line";
        try {
            if (firstSymbol && map.getLayer(ADMIN_ASSIGNMENT_HIGHLIGHT_FILL_LAYER_ID)) {
                map.moveLayer(ADMIN_ASSIGNMENT_HIGHLIGHT_FILL_LAYER_ID, firstSymbol);
            }
            if (firstSymbol && map.getLayer(ADMIN_ASSIGNMENT_HIGHLIGHT_LINE_LAYER_ID)) {
                map.moveLayer(ADMIN_ASSIGNMENT_HIGHLIGHT_LINE_LAYER_ID, firstSymbol);
            }
            if (map.getLayer(overviewFillId)) {
                map.moveLayer(overviewFillId, ADMIN_ASSIGNMENT_HIGHLIGHT_FILL_LAYER_ID);
            }
            if (map.getLayer(overviewLineId)) {
                map.moveLayer(overviewLineId, ADMIN_ASSIGNMENT_HIGHLIGHT_FILL_LAYER_ID);
            }
        } catch {
            // layer ordering race
        }

        return true;
    } catch (e) {
        console.warn("[MapView] Admin assignment highlight failed:", e);
        return false;
    }
}

export function removeAdminAssignmentHighlightFromMap(map: mapboxgl.Map): void {
    try {
        if (map.getLayer(ADMIN_ASSIGNMENT_HIGHLIGHT_FILL_LAYER_ID)) {
            map.removeLayer(ADMIN_ASSIGNMENT_HIGHLIGHT_FILL_LAYER_ID);
        }
        if (map.getLayer(ADMIN_ASSIGNMENT_HIGHLIGHT_LINE_LAYER_ID)) {
            map.removeLayer(ADMIN_ASSIGNMENT_HIGHLIGHT_LINE_LAYER_ID);
        }
        if (map.getSource(ADMIN_ASSIGNMENT_HIGHLIGHT_SOURCE_ID)) {
            map.removeSource(ADMIN_ASSIGNMENT_HIGHLIGHT_SOURCE_ID);
        }
    } catch {
        // style reload race
    }
}
